import { useEffect } from "react";
import {
  SITE_NAME,
  SITE_ORIGIN,
  TWITTER_HANDLE,
  canonicalUrlFor,
  DEFAULT_SEO,
} from "./seo";

/**
 * Sets document.title, meta description, OG tags, Twitter Card tags, and canonical
 * URL for the current route. Restores previous values on unmount so SPA navigation
 * stays clean.
 *
 * Note: For non-JS crawlers (LinkedIn, Facebook, iMessage, Slack) the equivalent
 * tags are baked into per-route HTML at build time by `scripts/generate-route-html.js`.
 * This hook handles runtime updates for Google + JS-capable scrapers + browser tabs.
 *
 * Usage:
 *   useDocumentMeta({
 *     title: "Services · ForgedOps",
 *     description: "...",
 *     image: "https://forgedopshq.com/media/op_concrete_crew.jpg",
 *     imageAlt: "...",
 *     path: "/services",
 *     type: "website",
 *   });
 */
export function useDocumentMeta({
  title,
  description,
  image,
  imageAlt,
  path,
  type,
} = {}) {
  useEffect(() => {
    const resolvedTitle = title || DEFAULT_SEO.title;
    const resolvedDesc = description || DEFAULT_SEO.description;
    const resolvedImage = image || DEFAULT_SEO.image;
    const resolvedImageAlt = imageAlt || DEFAULT_SEO.imageAlt;
    const resolvedPath =
      path || (typeof window !== "undefined" ? window.location.pathname : "/");
    const resolvedType = type || "website";
    const canonical = canonicalUrlFor(resolvedPath);

    // Snapshot current values so we can restore on unmount
    const prev = {
      title: document.title,
      desc: getMetaContent('meta[name="description"]'),
      ogTitle: getMetaContent('meta[property="og:title"]'),
      ogDesc: getMetaContent('meta[property="og:description"]'),
      ogImage: getMetaContent('meta[property="og:image"]'),
      ogImageAlt: getMetaContent('meta[property="og:image:alt"]'),
      ogImageWidth: getMetaContent('meta[property="og:image:width"]'),
      ogImageHeight: getMetaContent('meta[property="og:image:height"]'),
      ogImageType: getMetaContent('meta[property="og:image:type"]'),
      ogUrl: getMetaContent('meta[property="og:url"]'),
      ogType: getMetaContent('meta[property="og:type"]'),
      ogSiteName: getMetaContent('meta[property="og:site_name"]'),
      twCard: getMetaContent('meta[name="twitter:card"]'),
      twSite: getMetaContent('meta[name="twitter:site"]'),
      twTitle: getMetaContent('meta[name="twitter:title"]'),
      twDesc: getMetaContent('meta[name="twitter:description"]'),
      twImage: getMetaContent('meta[name="twitter:image"]'),
      twImageAlt: getMetaContent('meta[name="twitter:image:alt"]'),
      canonical: getLinkHref('link[rel="canonical"]'),
    };

    document.title = resolvedTitle;
    setMeta("name", "description", resolvedDesc);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:title", resolvedTitle);
    setMeta("property", "og:description", resolvedDesc);
    setMeta("property", "og:image", resolvedImage);
    setMeta("property", "og:image:alt", resolvedImageAlt);
    // Optional dimensions are omitted instead of publishing invented sizes.
    document.querySelectorAll('meta[property="og:image:width"], meta[property="og:image:height"]').forEach(node=>node.remove());
    const imageExtension = resolvedImage.split("?")[0].split(".").pop().toLowerCase();
    setMeta("property", "og:image:type", imageExtension === "png" ? "image/png" : imageExtension === "webp" ? "image/webp" : "image/jpeg");
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", resolvedType);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:site", TWITTER_HANDLE);
    setMeta("name", "twitter:title", resolvedTitle);
    setMeta("name", "twitter:description", resolvedDesc);
    setMeta("name", "twitter:image", resolvedImage);
    setMeta("name", "twitter:image:alt", resolvedImageAlt);
    setLink("canonical", canonical);

    return () => {
      document.title = prev.title;
      restore('meta[name="description"]', prev.desc);
      restore('meta[property="og:title"]', prev.ogTitle);
      restore('meta[property="og:description"]', prev.ogDesc);
      restore('meta[property="og:image"]', prev.ogImage);
      restore('meta[property="og:image:alt"]', prev.ogImageAlt);
      restore('meta[property="og:image:width"]', prev.ogImageWidth);
      restore('meta[property="og:image:height"]', prev.ogImageHeight);
      restore('meta[property="og:image:type"]', prev.ogImageType);
      restore('meta[property="og:url"]', prev.ogUrl);
      restore('meta[property="og:type"]', prev.ogType);
      restore('meta[property="og:site_name"]', prev.ogSiteName);
      restore('meta[name="twitter:card"]', prev.twCard);
      restore('meta[name="twitter:site"]', prev.twSite);
      restore('meta[name="twitter:title"]', prev.twTitle);
      restore('meta[name="twitter:description"]', prev.twDesc);
      restore('meta[name="twitter:image"]', prev.twImage);
      restore('meta[name="twitter:image:alt"]', prev.twImageAlt);
      restoreLink('link[rel="canonical"]', prev.canonical);
    };
  }, [title, description, image, imageAlt, path, type]);
}

function getMetaContent(selector) {
  const el = document.querySelector(selector);
  return el ? el.getAttribute("content") : null;
}

function getLinkHref(selector) {
  const el = document.querySelector(selector);
  return el ? el.getAttribute("href") : null;
}

function setMeta(attr, attrValue, content) {
  let el = document.querySelector(`meta[${attr}="${attrValue}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function restore(selector, prevContent) {
  const el = document.querySelector(selector);
  if (!el) return;
  if (prevContent === null) {
    el.parentNode.removeChild(el);
  } else {
    el.setAttribute("content", prevContent);
  }
}

function restoreLink(selector, prevHref) {
  const el = document.querySelector(selector);
  if (!el) return;
  if (prevHref === null) {
    el.parentNode.removeChild(el);
  } else {
    el.setAttribute("href", prevHref);
  }
}

// Re-export for callers that want to use a single config import
export { SITE_NAME, SITE_ORIGIN };

